import type { ColorChoice } from './colors'
import type { Shape, Variation } from '~/types/grid.types'
import { slatShapeKey, slatShapeSchema } from '~/shared/slats'

export const WOOD_SLAT_PRODUCT_IDS = {
  fourFoot: '9217478983908',
  eightFoot: '9211342946532',
} as const

export const WOOD_SLAT_FINISHES: ColorChoice[] = [
  { label: 'Oak / Black', image: '/new-studio/shapes/wood-slats/oak-black.png', admin: true, active: true },
  { label: 'Oak / Latte', image: '/new-studio/shapes/wood-slats/oak-latte.png', admin: true, active: true },
  { label: 'Walnut / Black', image: '/new-studio/shapes/wood-slats/walnut-black.png', admin: true, active: true },
  { label: 'Teak / Black', image: '/new-studio/shapes/wood-slats/teak-black.png', admin: true, active: true },
  { label: 'Wenge / Black', image: '/new-studio/shapes/wood-slats/wenge-black.png', admin: true, active: true },
]

const FOUR_FOOT_FINISHES = WOOD_SLAT_FINISHES.slice(0, 4)

export type WoodSlatShapeId =
  | 'WoodSlat4Ft'
  | 'WoodSlat8Ft'
  | 'WoodSlat4FtFreeform'
  | 'WoodSlat8FtFreeform'

export const isWoodSlatShapeId = (shapeId: string | undefined): shapeId is WoodSlatShapeId =>
  Boolean(shapeId?.startsWith('WoodSlat'))

export const getWoodSlatFinishChoices = (shapeId: string | undefined) => {
  if (!isWoodSlatShapeId(shapeId)) return []
  return shapeId.includes('4Ft') ? FOUR_FOOT_FINISHES : WOOD_SLAT_FINISHES
}

export const isWoodSlatFinishLabel = (label: string | undefined) =>
  WOOD_SLAT_FINISHES.some(finish => finish.label === label)

export const woodSlatTexture = (label: string | undefined) => {
  const finish = WOOD_SLAT_FINISHES.find(finish => normalizeFinish(label).includes(normalizeFinish(finish.label)))
  return finish?.image.replace(/\.png$/, '-texture.png')
}

const normalizeFinish = (value: string | null | undefined) =>
  (value || '')
    .toLowerCase()
    .replace(/felt/g, '')
    .replace(/[^a-z0-9]+/g, '')

export const matchWoodSlatVariation = <T extends Pick<Variation, 'label' | 'product_id' | 'shape_key'>>(
  rows: T[],
  shapeId: string,
  finishLabel: string,
): T | undefined => {
  if (!isWoodSlatShapeId(shapeId)) return undefined
  if (!getWoodSlatFinishChoices(shapeId).some(finish => finish.label === finishLabel)) return undefined

  const key = shapeId.replace(/Freeform$/, '')
  const requestedFinish = normalizeFinish(finishLabel)
  return rows.find(row =>
    slatShapeKey(row) === key
    && normalizeFinish(row.label).includes(requestedFinish),
  )
}

const baseShape = {
  type: 'rect',
  icon: 'WoodSlats',
  rotation: 0,
  rotationLocked: true,
  finishFamily: 'wood-slat',
  admin: true,
  quantity: 1,
  depthMm: 22.225,
  physicalWidthIn: 25.2,
} as const

export const WOOD_SLAT_SQUARE_SHAPES: Shape[] = [
  {
    ...baseShape,
    id: 'WoodSlat4Ft',
    prefix: 'WoodSlat4Ft',
    label: '4 ft Wood Slat Two-Pack',
    productId: WOOD_SLAT_PRODUCT_IDS.fourFoot,
    physicalHeightIn: 47.245,
    allowedColors: FOUR_FOOT_FINISHES.map(finish => finish.label),
    w: 252,
    h: 472.45,
  },
  {
    ...baseShape,
    id: 'WoodSlat8Ft',
    prefix: 'WoodSlat8Ft',
    label: '8 ft Wood Slat Two-Pack',
    productId: WOOD_SLAT_PRODUCT_IDS.eightFoot,
    physicalHeightIn: 94.49,
    allowedColors: WOOD_SLAT_FINISHES.map(finish => finish.label),
    w: 252,
    h: 944.9,
  },
]

export const WOOD_SLAT_FREEFORM_SHAPES: Shape[] = [
  {
    ...WOOD_SLAT_SQUARE_SHAPES[0],
    id: 'WoodSlat4FtFreeform',
    w: 252,
    h: 472.45,
  },
  {
    ...WOOD_SLAT_SQUARE_SHAPES[1],
    id: 'WoodSlat8FtFreeform',
    w: 252,
    h: 944.9,
  },
]

export const woodSlatPrefixForProduct = (productId: string | number | null | undefined) => {
  const normalized = String(productId || '')
  if (normalized === WOOD_SLAT_PRODUCT_IDS.fourFoot) return 'WoodSlat4Ft' as const
  if (normalized === WOOD_SLAT_PRODUCT_IDS.eightFoot) return 'WoodSlat8Ft' as const
  return undefined
}

/** Catalog dimensions are physical inches; Studio coordinates use 10 px/in. */
export const withSlatDimensions = (shape: Shape, variation: Pick<Variation, 'studio_shape'>): Shape => {
  if (!isWoodSlatShapeId(shape.id)) return shape
  const parsed = slatShapeSchema.safeParse(variation.studio_shape)
  if (!parsed.success || parsed.data.key !== shape.id.replace(/Freeform$/, '')) return shape
  const dimensions = parsed.data
  return {
    ...shape,
    w: dimensions.width_in * 10,
    h: dimensions.height_in * 10,
    physicalWidthIn: dimensions.width_in,
    physicalHeightIn: dimensions.height_in,
    depthMm: dimensions.depth_in * 25.4,
  }
}

export const withPlacedSlatDimensions = (shape: Shape, placed?: any): Shape => {
  if (!isWoodSlatShapeId(shape.id) || !placed) return shape
  const width = Number(placed.width ?? placed.attrs?.width)
  const height = Number(placed.height ?? placed.attrs?.height)
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return shape
  const metadata = placed.metadata ?? placed.attrs?.metadata ?? {}
  return {
    ...shape, w: width, h: height,
    physicalWidthIn: metadata.physical_width_in ?? width / 10,
    physicalHeightIn: metadata.physical_height_in ?? height / 10,
    depthMm: metadata.depth_mm ?? shape.depthMm,
  }
}

type SlatBox = { x: number, y: number, w: number, h: number }

/**
 * Square-grid snapping for slats. Slats are 25.2" wide, which is not a multiple
 * of the 3" grid step, so snapping their center (like tiles) keeps them off the
 * origin and prevents flush placement. Snap the top-left corner to the grid
 * instead, then pull edges flush against nearby slats within one grid step.
 * Positions are center-anchored, matching the canvas item coordinates.
 */
export const snapWoodSlatPosition = (
  point: { x: number, y: number },
  size: { w: number, h: number },
  grid: { origin: number, stepX: number, stepY: number },
  neighbours: SlatBox[],
) => {
  const rawLeft = point.x - size.w / 2
  const rawTop = point.y - size.h / 2
  const toGrid = (value: number, step: number) =>
    grid.origin + Math.round((value - grid.origin) / step) * step

  const gap = (startA: number, lengthA: number, startB: number, lengthB: number) =>
    Math.max(startB - (startA + lengthA), startA - (startB + lengthB), 0)

  const nearest = (raw: number, fallback: number, candidates: number[], threshold: number) => {
    let best = fallback
    let bestDistance = threshold
    for (const candidate of candidates) {
      const distance = Math.abs(candidate - raw)
      if (distance <= bestDistance) {
        best = candidate
        bestDistance = distance
      }
    }
    return best
  }

  const boxes = neighbours.map(n => ({ left: n.x - n.w / 2, top: n.y - n.h / 2, w: n.w, h: n.h }))
  const xCandidates = boxes
    .filter(n => gap(rawTop, size.h, n.top, n.h) <= grid.stepY)
    .flatMap(n => [n.left + n.w, n.left - size.w, n.left])
  const yCandidates = boxes
    .filter(n => gap(rawLeft, size.w, n.left, n.w) <= grid.stepX)
    .flatMap(n => [n.top + n.h, n.top - size.h, n.top])

  const left = nearest(rawLeft, toGrid(rawLeft, grid.stepX), xCandidates, grid.stepX)
  const top = nearest(rawTop, toGrid(rawTop, grid.stepY), yCandidates, grid.stepY)
  return { x: left + size.w / 2, y: top + size.h / 2 }
}
